import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {CheckOutlined, CloseOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Checkbox, Input, Modal, Space} from 'antd';
import {observer} from 'mobx-react-lite';
import {appState} from 'store/AppStore';
import {VehicleState} from 'store/vehicle/VehicleState';
import {GarageSpotAttribute, GarageSpotAttributeDefinitionDto} from '../../../../../../PRMGApiClient';

interface GarageSpotAttributeSelectionModalProps {
  state: VehicleState;
  attributeValues: Array<GarageSpotAttribute>;
  open: boolean;
  onClose: () => void;
}

export interface AttributeDefinitions extends GarageSpotAttributeDefinitionDto {
  checked: boolean;
}

export const GarageSpotAttributeSelectionModal: React.FunctionComponent<GarageSpotAttributeSelectionModalProps> =
  observer(({state, open, onClose, attributeValues}) => {
    const {t} = useTranslation();
    const [attributeCreateMode, setAttributeCreateMode] = useState(false);

    const garageSpot = state.item.garageSpot;

    const definitionForm = useForm<GarageSpotAttributeDefinitionDto>({
      mode: 'onTouched',
      defaultValues: {name: ''},
    });

    // Find attribute values for this garage spot and sort them by attribute definition id
    const attributeDefinitions = React.useMemo(
      () => appState.attributeDefinitions.slice().sort((a, b) => (a.id ?? 0) - (b.id ?? 0)),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [appState.attributeDefinitions]
    );

    const {control, reset, handleSubmit} = useForm<{attributeDefinitions: AttributeDefinitions[]}>({
      mode: 'onTouched',
      defaultValues: {attributeDefinitions: attributeDefinitions.map((d) => ({...d, checked: false}))},
    });

    useEffect(() => {
      // Define if attribute value exists for this garage spot and set checked flag
      if (garageSpot) {
        const definitions = attributeDefinitions.map((item) => ({
          ...item,
          checked: !!attributeValues.find((a) => a.attributeDefinitionId === item.id),
        }));
        // Reset form with the updated values
        reset({attributeDefinitions: definitions});
      }
    }, [garageSpot, attributeValues, control, attributeDefinitions, reset]);

    const onSubmit = useCallback(
      (data: {attributeDefinitions: AttributeDefinitions[]}) => {
        // Find garage spot attributes to remove
        const toRemove = attributeValues.filter((item) =>
          data.attributeDefinitions.find((i) => i.id === item.attributeDefinitionId && !i.checked)
        );

        // Prepare new garage spot attributes to add
        const toAdd = data.attributeDefinitions.filter(
          (item) => !!item.checked && !attributeValues.map((i) => i.attributeDefinitionId).includes(item.id)
        );

        state.updateGarageSpotAttributes({toAdd, toRemove}).then(() => {
          onClose();
          state.onSuccess();
        });
      },
      [attributeValues, onClose, state]
    );

    const onCancel = useCallback(() => {
      // Reset form and close modal
      onClose();
      reset();
    }, [onClose, reset]);

    return (
      <Modal
        title="Select parking spot attributes"
        open={open}
        onCancel={onCancel}
        footer={[
          <Button key="cancel" onClick={onCancel}>
            {t('Cancel')}
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit(onSubmit)}>
            {t('Save')}
          </Button>,
        ]}
      >
        <Space direction="vertical" className="mt-4 w-full">
          <>
            {attributeDefinitions.map((attribute, index) => (
              <Controller
                key={attribute.id}
                control={control}
                name={`attributeDefinitions.${index}.checked`}
                render={({field: {value, onChange}}) => (
                  <Checkbox checked={value} onChange={() => onChange(!value)}>
                    {attribute.name}
                  </Checkbox>
                )}
              />
            ))}
            {attributeCreateMode ? (
              <div className="flex w-full items-center gap-4">
                <Controller
                  name="name"
                  control={definitionForm.control}
                  rules={{required: true}}
                  render={({field: {onChange}}) => (
                    <Input
                      onChange={(e) => onChange(e.target.value)}
                      placeholder={t('Enter attribute definition name')}
                    />
                  )}
                />
                <div className="flex gap-2">
                  <Button
                    type="link"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={definitionForm.handleSubmit((payload) => {
                      state.addAttributeDefinition(payload);
                      setAttributeCreateMode(false);
                      definitionForm.reset();
                    })}
                  />
                  <Button
                    type="link"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => setAttributeCreateMode(false)}
                  />
                </div>
              </div>
            ) : (
              <Button type="link" className="p-0" icon={<PlusOutlined />} onClick={() => setAttributeCreateMode(true)}>
                {t('Add new')}
              </Button>
            )}
          </>
        </Space>
      </Modal>
    );
  });
